import { reactive } from "vue"
import { useOpsApi } from "src/api/client"
import type { OnCallGap } from "src/generated/enums"
import { useAuthStore } from "./auth"

/**
 * On-call status; drives the home page's on-call card. Refreshed by the app-init poll loop and on
 * login, cleared on lock-out.
 */
export interface OnCallStore {
  /** `null` means 'not checked yet'. */
  staffed: boolean | null
  /** empty unless [staffed]. */
  name: string
  /**
   * Why nobody is on call; `null` while [staffed], and while unchecked.
   *
   * Without it, a weekday daytime is plain `staffed: false` and the card reports a gap that does not
   * exist: between the shifts there simply is no on-call duty.
   */
  gap: OnCallGap | null
  /** The same reason as a whole sentence, with the date; `null` as for [gap]. */
  reason: string | null
  updateCurrent(): Promise<void>
  clearCurrent(): void
}

const store: OnCallStore = reactive<OnCallStore>({
  staffed: null,
  name: "",
  gap: null,
  reason: null,

  async updateCurrent(): Promise<void> {
    if (useAuthStore().authenticated !== true) return
    const response = await useOpsApi().oncall.current()
    this.staffed = response.staffed
    this.name = response.name
    this.gap = response.gap ?? null
    this.reason = response.reason ?? null
  },

  clearCurrent(): void {
    this.staffed = null
    this.name = ""
    this.gap = null
    this.reason = null
  },
})

export function useOnCallStore(): OnCallStore {
  return store
}
